import { useParams, useRouteContext } from "@tanstack/react-router";
import { useState } from "react";
import { Tab, Tabs } from "react-bootstrap";

import LayoutContentArea from "@/components/layout/LayoutContentArea";
import StaffHeading from "@/components/records/staff/StaffIndividualHeading";
import SASSRecordsTab from "@/components/records/staff/tabs/SASSRecordsTab";
import SCLRRecordsTab from "@/components/records/staff/tabs/SCLRRecordsTab";
import STFFRecordsTab from "@/components/records/staff/tabs/STFFRecordsTab";
import { CLRM_Key } from "@/models/clrm";
import { useCoreData, useLeaData } from "@/queries.ts";
import { buildStaffHeadingModel } from "@/components/records/staff/staffHeadingModel";
import Breadcrumb from "@/components/shared/BreadCrumb";
import { formatDateOnly } from "@/utils/dates";

export default function StaffIndividualViewPage() {
  const [activeTab, setActiveTab] = useState("stff");
  const leaContext = useRouteContext({ from: "/$leaid" });

  const params = useParams({
    from: "/$leaid/_error-wrapper/records/staff/$staffId",
  });

  const leaid = params.leaid!;
  const staffId = params.staffId;

  const { data: lea, isLoading, error } = useLeaData(leaid);
  const coreData = useCoreData();

  const staffRoleList = coreData?.staffRoleList ?? [];
  const nonClassroomRoleList = coreData?.nonClassroomRoleList ?? [];

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <div className="spinner-border text-primary" role="status" />
        <span className="ms-2">Loading staff record...</span>
      </div>
    );
  }

  if (error) return <div>Error: {error.message}</div>;
  if (!lea) return <div>No LEA data found.</div>;

  const stffRecords = lea.entities.staff[staffId].records.map((record) => {
    const middleName = record.StaffLegalMiddleName
      ? ` ${record.StaffLegalMiddleName}`
      : "";
    return {
      ...record,
      StaffName:
        `${record.StaffLegalFirstName || ""}${middleName} ${record.StaffLegalLastName || ""}`.trim(),
      EffectiveDate: record.EffectiveStartDate
        ? formatDateOnly(record.EffectiveStartDate)
        : "",
    };
  });

  const staff = stffRecords[0];
  if (!staff) return <div>Staff record not found.</div>;

  const sclrRecords = (staff.ClassroomAssignments ?? []).map((record) => {
    const classroom = lea.entities.classrooms[CLRM_Key(record)]?.records[0];

    const preschoolName =
      leaContext?.currentOrgPreschools?.find(
        (x) => x.PreschoolCDSCode === record.PreschoolCode
      )?.PreschoolName ?? "Unknown preschool";

    return {
      ...record,
      PreschoolName: preschoolName,
      ClassroomName: classroom?.Id
        ? classroom.ClassroomName
        : `Classroom Id: ${record.LocalClassroomId}`,
    };
  });

  const sassRecords = (staff.StaffAssignments ?? []).map((record) => {
    const preschoolName =
      leaContext?.currentOrgPreschools?.find(
        (x) => x.PreschoolCDSCode === record.PreschoolCode
      )?.PreschoolName ?? "Unknown preschool";

    return {
      ...record,
      PreschoolName: preschoolName,
      ClassroomRole1Name: staffRoleList.find(
        (x) => x.Value === record.ClassroomRole1
      )?.Text,
      NonClassroomRole1Name: nonClassroomRoleList.find(
        (x) => x.Value === record.NonClassroomRole1
      )?.Text,
    };
  });

  const headingModel = buildStaffHeadingModel({
    staff,
    leaid,
    sassRecords,
    preschools: leaContext.currentOrgPreschools ?? [],
    staffRoleList,
    nonClassroomRoleList,
  });

  const breadcrumbItems = [
    {
      label: "Home",
      to: "/$leaid" as const,
      params: { leaid },
      ariaLabel: "Go to Home",
      isHome: true,
    },
    {
      label: "Staff Records",
      to: "/$leaid/records/staff" as const,
      params: { leaid },
    },
    {
      label: headingModel.staffName,
    },
  ];

  return (
    <LayoutContentArea
      headingText={null}
      noBorder={true}
      showHeadingSection={false}
    >
      <Breadcrumb items={breadcrumbItems} />

      <div
        className="card rounded-4 border-0 mb-4"
        style={{
          boxShadow:
            "0 1px 3px 0 rgb(15 23 42 / 0.08), 0 1px 2px -1px rgb(15 23 42 / 0.06)",
        }}
      >
        <div className="card-custom shadow-none p-0">
          <div className="p-3 p-md-4">
            <StaffHeading
              staffName={headingModel.staffName}
              initials={headingModel.initials}
              leaid={headingModel.leaid}
              school={headingModel.school}
              seid={headingModel.seid}
              localId={headingModel.localId}
              primaryRole={headingModel.primaryRole}
            />
          </div>

          <Tabs
            className="section-tabs"
            fill
            activeKey={activeTab}
            onSelect={(key) => setActiveTab(key || "")}
          >
            {leaContext.hasRole("STFFView") && (
              <Tab eventKey="stff" title="Staff (STFF)" className="pt-3">
                {activeTab === "stff" && (
                  <STFFRecordsTab
                    stffRecords={stffRecords}
                    staff={staff}
                    sassRecords={sassRecords}
                    sclrRecords={sclrRecords}
                  />
                )}
              </Tab>
            )}

            {leaContext.hasRole("SASSView") && (
              <Tab
                eventKey="sass"
                title="Staff Assignment (SASS)"
                className="pt-3"
              >
                {activeTab === "sass" && (
                  <SASSRecordsTab sassRecords={sassRecords} staff={staff} />
                )}
              </Tab>
            )}

            {leaContext.hasRole("SCLRView") && (
              <Tab
                eventKey="sclr"
                title="Staff Classroom (SCLR)"
                className="pt-3"
              >
                {activeTab === "sclr" && (
                  <SCLRRecordsTab sclrRecords={sclrRecords} staff={staff} />
                )}
              </Tab>
            )}
          </Tabs>
        </div>
      </div>
    </LayoutContentArea>
  );
}
